import React, { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import {
  ArrowRight,
  ArrowLeft,
  Calendar,
  CalendarClock,
  CheckCircle,
  Clock,
  FilePlus2,
  Loader2,
  MapPin,
  PlaneTakeoff,
  Sparkles,
  ScrollText,
  TimerReset,
  TrendingUp,
  XCircle,
} from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, CartesianGrid, Tooltip, XAxis, YAxis } from 'recharts';
import { toast } from 'sonner';

import { leaveAPI } from '../services/api';
import { getErrorMessage } from '../lib/utils';
import { tooltipContentStyle, tooltipCursor, axisProps, gridProps } from '../lib/chartTheme';
import { EmptyState, CardSkeleton } from './common/States';
import { StatCard } from './common/StatCard';
import LeaveSection from './LeaveSection';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { useTableControls } from '../hooks/useTableControls';
import { TableControls } from './ui/table-controls';

const EmployeeAttendanceMenu = ({
  records,
  todayRecord,
  chartData,
  loading,
  attemptType,
  canCheckIn,
  canCheckOut,
  onMarkAttendance,
  formatDateTime,
}) => {
  const [currentView, setCurrentView] = useState('menu');
  const [requestForm, setRequestForm] = useState({
    date: new Date().toISOString().split('T')[0],
    start_time: '',
    end_time: '',
    reason: '',
  });
  const [submittingRequest, setSubmittingRequest] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [shortLeaves, setShortLeaves] = useState([]);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [compOffLoading, setCompOffLoading] = useState(false);
  const [compOffData, setCompOffData] = useState({ balance: 0, total_earned: 0, total_used: 0, entries: [] });
  const [preferredLeaveType, setPreferredLeaveType] = useState('casual');

  useEffect(() => {
    if (currentView === 'request-history') {
      fetchShortLeaves();
    }
    if (currentView === 'leave-balances') {
      fetchLeaveBalance();
    }
    if (currentView === 'comp-off') {
      fetchCompOffs();
    }
  }, [currentView]);

  const fetchShortLeaves = async () => {
    setHistoryLoading(true);
    try {
      const response = await leaveAPI.listShortLeaves();
      setShortLeaves(Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      setShortLeaves([]);
      toast.error(getErrorMessage(error, 'Failed to fetch request history'));
    } finally {
      setHistoryLoading(false);
    }
  };

  const fetchLeaveBalance = async () => {
    setBalanceLoading(true);
    try {
      const response = await leaveAPI.getBalance();
      setLeaveBalance(response?.data || null);
    } catch (error) {
      setLeaveBalance(null);
      toast.error(getErrorMessage(error, 'Failed to fetch leave balance'));
    } finally {
      setBalanceLoading(false);
    }
  };

  const fetchCompOffs = async () => {
    setCompOffLoading(true);
    try {
      const response = await leaveAPI.getCompOffs();
      setCompOffData(response?.data || { balance: 0, total_earned: 0, total_used: 0, entries: [] });
    } catch (error) {
      setCompOffData({ balance: 0, total_earned: 0, total_used: 0, entries: [] });
      toast.error(getErrorMessage(error, 'Failed to fetch comp off history'));
    } finally {
      setCompOffLoading(false);
    }
  };

  const handleRequestSubmit = async (event) => {
    event.preventDefault();
    setSubmittingRequest(true);
    try {
      await leaveAPI.applyShortLeave(requestForm);
      toast.success('Attendance request submitted');
      setRequestForm({
        date: new Date().toISOString().split('T')[0],
        start_time: '',
        end_time: '',
        reason: '',
      });
      setCurrentView('request-history');
      fetchShortLeaves();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to submit request'));
    } finally {
      setSubmittingRequest(false);
    }
  };

  const totalWeeklyHours = chartData.reduce((sum, item) => sum + (item.hours || 0), 0);
  const workedDays = chartData.filter((item) => (item.hours || 0) > 0).length;
  const punctualityRate = useMemo(() => {
    if (!records.length) return 0;
    const onTime = records.filter((record) => {
      const status = (record.status || '').toUpperCase();
      return status === 'PRESENT' || status === 'ON_TIME';
    }).length;
    return (onTime / records.length) * 100;
  }, [records]);
  const recentLogs = useMemo(
    () =>
      [...records]
        .sort((a, b) => new Date(b.check_in_time || b.date || 0) - new Date(a.check_in_time || a.date || 0))
        .slice(0, 5),
    [records]
  );
  const recentLogsTable = useTableControls(recentLogs, {
    initialSortKey: null,
    initialSortDirection: 'desc',
  });

  const getStatusBadgeVariant = (status) => {
    const normalized = String(status || '').toLowerCase();
    if (normalized === 'present' || normalized === 'on_time') return 'success';
    if (normalized === 'late') return 'warning';
    if (normalized === 'absent') return 'danger';
    return 'outline';
  };

  const menuSections = [
    {
      title: 'Attendance',
      items: [
        {
          id: 'clock-in',
          title: 'Clock In',
          description: 'You can clock-in from here',
          icon: MapPin,
        },
        {
          id: 'raise-request',
          title: 'Raise Request',
          description: 'Request WFH, on duty, overtime and partial day',
          icon: FilePlus2,
        },
        {
          id: 'logs-and-shifts',
          title: 'Logs and Shifts',
          description: 'View all attendance logs here',
          icon: TimerReset,
        },
        {
          id: 'request-history',
          title: 'Request History',
          description: 'View history for attendance requests',
          icon: ScrollText,
        },
      ],
    },
    {
      title: 'Leave',
      items: [
        {
          id: 'leave-apply',
          title: 'Apply Leave',
          description: 'Apply for your next leave here',
          icon: Calendar,
        },
        {
          id: 'leave-balances',
          title: 'Leave Balances',
          description: 'View your leave balances, history and transactions',
          icon: CalendarClock,
        },
        {
          id: 'comp-off',
          title: 'Comp Off',
          description: 'Request for comp off, view balances and history',
          icon: PlaneTakeoff,
        },
      ],
    },
  ];

  const viewTitle = {
    'clock-in': 'Clock In',
    'raise-request': 'Raise Request',
    'logs-and-shifts': 'Logs and Shifts',
    'request-history': 'Request History',
    'leave-apply': 'Apply Leave',
    'leave-balances': 'Leave Balances',
    'comp-off': 'Comp Off',
  };

  const renderMenu = () => (
    <div className="space-y-8 animate-fade-in">
      <section className="space-y-4">
        <div>
          <h2 className="text-hr-h2 font-bold tracking-tight text-foreground">Attendance Snapshot</h2>
          <p className="text-hr-sm text-muted-foreground">Actionable overview with quick actions, trends, and latest logs.</p>
        </div>
        <Card className="border-primary/20 bg-gradient-to-r from-primary/10 via-card to-[hsl(var(--chart-2))]/10">
          <CardContent className="pt-5">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-hr-xs bg-background">
                  <Sparkles className="h-3.5 w-3.5 text-primary" />
                  Snapshot Insight
                </div>
                <p className="text-hr-sm mt-2 text-muted-foreground">
                  Your punctuality is <span className="font-semibold text-foreground">{punctualityRate.toFixed(1)}%</span> with {totalWeeklyHours.toFixed(1)}h this week.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="success">On Time {punctualityRate.toFixed(0)}%</Badge>
                <Badge variant="info">Worked Days {workedDays}</Badge>
              </div>
            </div>
          </CardContent>
        </Card>
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <Card className="border-primary/20 bg-primary/5 cursor-pointer hover:shadow-md transition-all" onClick={() => setCurrentView('clock-in')}>
            <CardHeader className="pb-2">
              <CardTitle className="text-hr-sm font-medium">Today Status</CardTitle>
            </CardHeader>
            <CardContent>
              <Badge variant="outline">{todayRecord?.status || 'Not marked'}</Badge>
              <p className="mt-2 inline-flex items-center gap-1 text-primary text-hr-xs font-medium">
                Open Clock In <ArrowRight className="h-3.5 w-3.5" />
              </p>
            </CardContent>
          </Card>
          <Card className="border-[hsl(var(--chart-2))]/20 bg-[hsl(var(--chart-2))]/10 cursor-pointer hover:shadow-md transition-all" onClick={() => setCurrentView('clock-in')}>
            <CardHeader className="pb-2">
              <CardTitle className="text-hr-sm font-medium">Check In</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-hr-h2 font-bold">{formatDateTime(todayRecord?.check_in_time, 'hh:mm a', '--:--')}</div>
              <p className="mt-2 inline-flex items-center gap-1 text-[hsl(var(--chart-2))] text-hr-xs font-medium">
                Mark Attendance <ArrowRight className="h-3.5 w-3.5" />
              </p>
            </CardContent>
          </Card>
          <Card className="border-[hsl(var(--chart-3))]/20 bg-[hsl(var(--chart-3))]/10 cursor-pointer hover:shadow-md transition-all" onClick={() => setCurrentView('logs-and-shifts')}>
            <CardHeader className="pb-2">
              <CardTitle className="text-hr-sm font-medium">Hours This Week</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-hr-h2 font-bold">{totalWeeklyHours.toFixed(1)}h</div>
              <p className="mt-2 inline-flex items-center gap-1 text-[hsl(var(--chart-3))] text-hr-xs font-medium">
                View Trends <ArrowRight className="h-3.5 w-3.5" />
              </p>
            </CardContent>
          </Card>
          <Card className="border-[hsl(var(--chart-4))]/20 bg-[hsl(var(--chart-4))]/10 cursor-pointer hover:shadow-md transition-all" onClick={() => setCurrentView('request-history')}>
            <CardHeader className="pb-2">
              <CardTitle className="text-hr-sm font-medium">Worked Days</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-hr-h2 font-bold">{workedDays}</div>
              <p className="mt-2 inline-flex items-center gap-1 text-[hsl(var(--chart-4))] text-hr-xs font-medium">
                Open Requests <ArrowRight className="h-3.5 w-3.5" />
              </p>
            </CardContent>
          </Card>
        </div>
        <div className="grid gap-6 lg:grid-cols-2">
          <Card className="border-border shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-primary" />
                Weekly Hours Trend
              </CardTitle>
              <CardDescription>Last 7 attendance entries</CardDescription>
            </CardHeader>
            <CardContent className="h-[260px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid {...gridProps} />
                  <XAxis dataKey="date" {...axisProps} />
                  <YAxis allowDecimals={false} {...axisProps} />
                  <Tooltip cursor={tooltipCursor} contentStyle={tooltipContentStyle} />
                  <Bar dataKey="hours" fill="hsl(var(--primary))" radius={[6, 6, 0, 0]} name="Hours Worked" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
          <Card className="border-border shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Recent Logs</CardTitle>
                <CardDescription>Latest attendance records</CardDescription>
              </div>
              <Button size="sm" variant="outline" onClick={() => setCurrentView('logs-and-shifts')}>
                Open Logs
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-hr-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-3">Date</th>
                      <th className="text-left p-3">In</th>
                      <th className="text-left p-3">Out</th>
                      <th className="text-left p-3">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentLogsTable.data.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="p-4 text-center text-muted-foreground">
                          No attendance logs found.
                        </td>
                      </tr>
                    ) : (
                      recentLogsTable.data.map((record) => (
                        <tr key={record.id} className="border-b hover:bg-muted/40 cursor-pointer" onClick={() => setCurrentView('logs-and-shifts')}>
                          <td className="p-3">{formatDateTime(record.check_in_time || record.date, 'dd MMM yyyy', '--')}</td>
                          <td className="p-3">{formatDateTime(record.check_in_time, 'hh:mm a', '--:--')}</td>
                          <td className="p-3">{formatDateTime(record.check_out_time, 'hh:mm a', '--:--')}</td>
                          <td className="p-3">
                            <Badge variant={getStatusBadgeVariant(record.status)}>
                              {record.status || 'Not marked'}
                            </Badge>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <div className="p-3 pt-0">
                <TableControls
                  totalRows={recentLogsTable.totalRows}
                  page={recentLogsTable.page}
                  totalPages={recentLogsTable.totalPages}
                  pageSize={recentLogsTable.pageSize}
                  pageSizes={recentLogsTable.pageSizes}
                  onPageSizeChange={recentLogsTable.onPageSizeChange}
                  onPrevious={recentLogsTable.goToPreviousPage}
                  onNext={recentLogsTable.goToNextPage}
                />
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {menuSections.map((section) => (
        <section key={section.title} className="space-y-4">
          <div>
          <h2 className="text-hr-h2 font-bold tracking-tight text-foreground">{section.title}</h2>
          </div>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {section.items.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setCurrentView(item.id)}
                  className="text-left"
                >
                  <Card className="h-full border-border bg-card transition-all duration-200 hover:border-primary/30 hover:shadow-md hover:-translate-y-0.5">
                    <CardHeader className="space-y-4">
                      <div className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="space-y-1">
                        <CardTitle className="text-hr-lg">{item.title}</CardTitle>
                        <CardDescription className="text-hr-sm leading-6">{item.description}</CardDescription>
                      </div>
                    </CardHeader>
                  </Card>
                </button>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );

  const renderSubViewHeader = () => (
    <div className="flex items-center justify-between gap-4">
      <div>
        <h2 className="text-hr-h2 font-bold tracking-tight text-foreground">{viewTitle[currentView]}</h2>
      </div>
      <Button type="button" variant="outline" onClick={() => setCurrentView('menu')}>
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back
      </Button>
    </div>
  );

  const renderClockInView = () => (
    <div className="space-y-6">
      {renderSubViewHeader()}
      <div className="grid md:grid-cols-2 gap-6">
        <Card className="border-border shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-primary" />
              Mark Attendance
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <Button
                type="button"
                onClick={() => onMarkAttendance('check_in')}
                disabled={loading || !canCheckIn}
                className="h-28 flex-col gap-3"
                variant={canCheckIn ? 'default' : 'secondary'}
              >
                {loading && attemptType === 'check_in' ? (
                  <Loader2 className="h-8 w-8 animate-spin" />
                ) : (
                  <CheckCircle className="h-8 w-8" />
                )}
                <span className="text-hr-lg font-semibold">Check In</span>
              </Button>

              <Button
                type="button"
                onClick={() => onMarkAttendance('check_out')}
                disabled={loading || !canCheckOut}
                variant="outline"
                className="h-28 flex-col gap-3"
              >
                {loading && attemptType === 'check_out' ? (
                  <Loader2 className="h-8 w-8 animate-spin" />
                ) : (
                  <XCircle className="h-8 w-8" />
                )}
                <span className="text-hr-lg font-semibold">Check Out</span>
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Card className="border-primary/20 bg-primary/5">
                <CardHeader className="pb-2">
                  <CardDescription>Check-in Time</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-hr-h2 font-bold">{formatDateTime(todayRecord?.check_in_time, 'hh:mm a')}</div>
                </CardContent>
              </Card>
              <Card className="border-primary/20 bg-primary/5">
                <CardHeader className="pb-2">
                  <CardDescription>Check-out Time</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-hr-h2 font-bold">{formatDateTime(todayRecord?.check_out_time, 'hh:mm a', '--:--')}</div>
                </CardContent>
              </Card>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border shadow-sm">
          <CardHeader>
            <CardTitle>Today</CardTitle>
            <CardDescription>Your latest attendance status</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between rounded-lg border p-4">
              <span className="text-hr-sm text-muted-foreground">Status</span>
              <Badge variant="outline">{todayRecord?.status || 'Not marked'}</Badge>
            </div>
            <div className="flex items-center justify-between rounded-lg border p-4">
              <span className="text-hr-sm text-muted-foreground">Hours</span>
              <span className="font-semibold">
                {todayRecord?.total_hours ? `${todayRecord.total_hours.toFixed(1)}h` : '-'}
              </span>
            </div>
            <div className="rounded-lg border p-4 text-hr-sm text-muted-foreground">
              Use this page to check in and check out for the day. Once attendance is marked, your latest log appears in Logs and Shifts.
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  const renderLogsView = () => (
    <div className="space-y-6">
      {renderSubViewHeader()}
      <div className="grid md:grid-cols-2 gap-6">
        <Card className="border-border shadow-sm">
          <CardHeader>
            <CardTitle>Attendance Logs</CardTitle>
            <CardDescription>Your latest attendance entries</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
              {records.slice(0, 10).map((record) => (
                <div key={record.id} className="flex items-center justify-between rounded-lg border p-4">
                  <div>
                    <p className="font-medium">{formatDateTime(record.check_in_time, 'MMM dd, yyyy')}</p>
                    <p className="text-sm text-muted-foreground">
                      {formatDateTime(record.check_in_time, 'hh:mm a')} -
                      {record.check_out_time ? ` ${formatDateTime(record.check_out_time, 'hh:mm a')}` : ' Ongoing'}
                    </p>
                  </div>
                  <div className="text-right">
                    <Badge variant="outline">{record.status}</Badge>
                    {record.total_hours && (
                      <p className="mt-1 text-xs text-muted-foreground">{record.total_hours.toFixed(1)}h</p>
                    )}
                  </div>
                </div>
              ))}
              {records.length === 0 && (
                <EmptyState
                  compact
                  icon={CalendarClock}
                  title="No attendance logs found"
                  description="Your attendance entries will appear here once you start checking in."
                />
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="border-border shadow-sm">
          <CardHeader>
            <CardTitle>Weekly Hours</CardTitle>
            <CardDescription>Shift assignment details will appear here once they are configured</CardDescription>
          </CardHeader>
          <CardContent className="h-[420px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid {...gridProps} />
                <XAxis dataKey="date" {...axisProps} />
                <YAxis allowDecimals={false} {...axisProps} />
                <Tooltip cursor={tooltipCursor} contentStyle={tooltipContentStyle} />
                <Bar dataKey="hours" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} name="Hours Worked" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  const renderRaiseRequestView = () => (
    <div className="space-y-6">
      {renderSubViewHeader()}
      <Card className="border-border shadow-sm">
        <CardHeader>
          <CardTitle>Attendance Request</CardTitle>
          <CardDescription>Submit a time-based attendance request using the short leave workflow</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleRequestSubmit} className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="attendance-request-date">Date</Label>
              <Input
                id="attendance-request-date"
                type="date"
                value={requestForm.date}
                onChange={(event) => setRequestForm((prev) => ({ ...prev, date: event.target.value }))}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="attendance-request-start">Start Time</Label>
              <Input
                id="attendance-request-start"
                type="time"
                value={requestForm.start_time}
                onChange={(event) => setRequestForm((prev) => ({ ...prev, start_time: event.target.value }))}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="attendance-request-end">End Time</Label>
              <Input
                id="attendance-request-end"
                type="time"
                value={requestForm.end_time}
                onChange={(event) => setRequestForm((prev) => ({ ...prev, end_time: event.target.value }))}
                required
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="attendance-request-reason">Reason</Label>
              <Textarea
                id="attendance-request-reason"
                value={requestForm.reason}
                onChange={(event) => setRequestForm((prev) => ({ ...prev, reason: event.target.value }))}
                rows={4}
                required
              />
            </div>
            <div className="md:col-span-2 flex items-center justify-between gap-4 rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
              <span>Use this request page for employee attendance adjustments such as short leave or partial-day time requests.</span>
              <Button type="submit" disabled={submittingRequest}>
                {submittingRequest && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Submit Request
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );

  const renderRequestHistoryView = () => (
    <div className="space-y-6">
      {renderSubViewHeader()}
      <Card className="border-border shadow-sm">
        <CardHeader>
          <CardTitle>Attendance Request History</CardTitle>
          <CardDescription>Your submitted short leave and attendance requests</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {historyLoading && <CardSkeleton rows={4} />}
          {!historyLoading && shortLeaves.map((request) => (
            <div key={request.id} className="flex items-start justify-between rounded-lg border p-4">
              <div className="space-y-1">
                <p className="font-medium">{format(new Date(request.date), 'MMM dd, yyyy')}</p>
                <p className="text-sm text-muted-foreground">
                  {request.start_time} - {request.end_time} ({request.duration_hours} hrs)
                </p>
                <p className="text-sm text-muted-foreground">{request.reason}</p>
              </div>
              <Badge variant="outline">{request.status}</Badge>
            </div>
          ))}
          {!historyLoading && shortLeaves.length === 0 && (
            <EmptyState
              compact
              icon={CalendarClock}
              title="No request history found"
              description="Your submitted short leave and attendance requests will appear here."
            />
          )}
        </CardContent>
      </Card>
    </div>
  );

  const renderLeaveBalancesView = () => (
    <div className="space-y-6">
      {renderSubViewHeader()}
      {balanceLoading ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
          {Array.from({ length: 5 }).map((_, i) => (
            <CardSkeleton key={i} rows={1} />
          ))}
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
          <StatCard label="Annual Leave" value={leaveBalance?.annual_leave ?? 0} icon={Calendar} tone="primary" />
          <StatCard label="Casual Leave" value={leaveBalance?.casual_leave ?? 0} icon={PlaneTakeoff} tone="info" />
          <StatCard label="Sick Leave" value={leaveBalance?.sick_leave ?? 0} icon={CalendarClock} tone="purple" />
          <StatCard label="Comp Off" value={leaveBalance?.compensatory_leave ?? 0} icon={TimerReset} tone="success" />
          <StatCard label="Carry Forward Annual" value={leaveBalance?.carried_forward_annual ?? 0} icon={TrendingUp} tone="slate" />
          <StatCard label="Carry Forward Sick" value={leaveBalance?.carried_forward_sick ?? 0} icon={TrendingUp} tone="slate" />
        </div>
      )}
      <Card className="border-border shadow-sm">
        <CardHeader>
          <CardTitle>Balance Summary</CardTitle>
          <CardDescription>Current leave year: {leaveBalance?.year || new Date().getFullYear()}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3">
          <Button type="button" onClick={() => setCurrentView('leave-apply')}>
            Apply Leave
          </Button>
          <Button type="button" variant="outline" onClick={() => setCurrentView('request-history')}>
            View Attendance Requests
          </Button>
        </CardContent>
      </Card>
    </div>
  );

  const renderCompOffView = () => (
    <div className="space-y-6">
      {renderSubViewHeader()}
      <Card className="border-border shadow-sm">
        <CardHeader>
          <CardTitle>Comp Off</CardTitle>
          <CardDescription>Earn 1 comp off for each present attendance marked on a weekly off or mandatory holiday</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {compOffLoading ? (
            <div className="grid gap-4 md:grid-cols-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <CardSkeleton key={i} rows={1} />
              ))}
            </div>
          ) : (
            <>
              <div className="grid gap-4 md:grid-cols-3">
                <StatCard label="Available" value={compOffData?.balance ?? 0} icon={PlaneTakeoff} tone="warning" />
                <StatCard label="Earned" value={compOffData?.total_earned ?? 0} icon={CheckCircle} tone="success" />
                <StatCard label="Used" value={compOffData?.total_used ?? 0} icon={TimerReset} tone="slate" />
              </div>

              <div className="space-y-3">
                {(compOffData?.entries || []).map((entry) => (
                  <div key={entry.id} className="flex items-center justify-between rounded-lg border p-4">
                    <div>
                      <p className="font-medium">
                        {entry.source_type === 'holiday' ? 'Holiday Worked' : 'Weekly Off Worked'}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {format(new Date(entry.attendance_date), 'MMM dd, yyyy')}
                        {entry.source_label ? ` • ${entry.source_label}` : ''}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">{entry.remaining_days} / {entry.credited_days} available</p>
                      <Badge variant={entry.remaining_days > 0 ? 'secondary' : 'outline'}>
                        {entry.status.replace('_', ' ').toUpperCase()}
                      </Badge>
                    </div>
                  </div>
                ))}
                {(!compOffData?.entries || compOffData.entries.length === 0) && (
                  <EmptyState
                    compact
                    icon={CalendarClock}
                    title="No comp off earned yet"
                    description="Mark attendance on a weekly off or mandatory holiday to earn 1 comp off."
                  />
                )}
              </div>

              <div className="flex flex-wrap gap-3">
                <Button
                  type="button"
                  onClick={() => {
                    setPreferredLeaveType('compensatory');
                    setCurrentView('leave-apply');
                  }}
                >
                  Apply Comp Off
                </Button>
                <Button type="button" variant="outline" onClick={() => setCurrentView('leave-balances')}>
                  View Leave Balances
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );

  if (currentView === 'clock-in') {
    return renderClockInView();
  }

  if (currentView === 'raise-request') {
    return renderRaiseRequestView();
  }

  if (currentView === 'logs-and-shifts') {
    return renderLogsView();
  }

  if (currentView === 'request-history') {
    return renderRequestHistoryView();
  }

  if (currentView === 'leave-apply') {
    return (
      <div className="space-y-6">
        {renderSubViewHeader()}
        <LeaveSection currentTab="apply" initialLeaveType={preferredLeaveType} />
      </div>
    );
  }

  if (currentView === 'leave-balances') {
    return renderLeaveBalancesView();
  }

  if (currentView === 'comp-off') {
    return renderCompOffView();
  }

  return renderMenu();
};

export default EmployeeAttendanceMenu;
